/**
 * Detects the one server-side condition both Sites forms need to distinguish from a generic
 * failure: a duplicate `internalCode` within the caller's own company (the DB's
 * `(company_id, internalCode)` unique constraint on `Site` — see `Site.java`). Shared between
 * `NewSiteForm.tsx` and `EditSiteForm.tsx` rather than duplicated (unlike the two forms' role
 * option tables elsewhere in this codebase) because this isn't a form-specific rule, it's the
 * shape of one HTTP response — the same reasoning that keeps it out of each form's own JSX.
 *
 * The two mutations this guards (`POST /site`, `PUT /site/{id}`) surface the SAME underlying
 * constraint violation through two DIFFERENT response shapes, confirmed against the live backend
 * (`feat/sites-flow`) rather than assumed from source alone:
 *
 * - `PUT /site/{id}` (`SiteController#updateSite`) catches the `DataIntegrityViolationException`
 *   itself and returns HTTP 400 with a body carrying `message: "Internal Code already in use."`
 *   — a clean, English-hardcoded string, not translated (see the controller's own catch block).
 * - `POST /site` (`SiteController#createSite`) does NOT catch it — the exception reaches
 *   `GlobalExceptionHandler#handleDataIntegrityViolationException`, which returns HTTP 409 with
 *   `errorType: "DUPLICATED"` and `message` set to the RAW Postgres constraint-violation text
 *   (`"could not execute statement [ERROR: duplicate key value violates unique constraint ..."`)
 *   — not something to show a user as-is.
 *
 * Both branches below are checked, and either alone is enough: `errorType === "DUPLICATED"`
 * catches the create path (whose message is unusable), and the `message` substring check catches
 * the edit path (whose `errorType` field is absent entirely, since it never reaches the global
 * handler). Neither form displays the backend's raw string either way — both show their own
 * translated `sites.codeExists` copy once this returns true.
 */
export function isDuplicateInternalCodeError(error: unknown): boolean {
  const response = (
    error as
      | { response?: { data?: { message?: unknown; errorType?: unknown } } }
      | undefined
  )?.response;
  if (!response?.data) return false;
  if (response.data.errorType === "DUPLICATED") return true;
  const message = response.data.message;
  return (
    typeof message === "string" &&
    message.toLowerCase().includes("internal code")
  );
}
